import type { PieceDetail, PieceDraft } from "@/types/piece";
import { formatContentType } from "@/lib/format";

interface Props {
  piece: PieceDetail;
  latestDraft: PieceDraft | null;
}

/**
 * The piece's own characteristics, as a section of the side column.
 *
 * FEAT-087 — editorial direction is deliberately not among these rows. It is
 * the brief the piece was written from, several sentences long, and it reads
 * in the main window with the piece rather than squeezed into a metadata
 * row in a 320px column.
 */
export function PieceCharacteristics({ piece, latestDraft }: Props) {
  const wordCount = latestDraft?.content
    ? latestDraft.content.trim().split(/\s+/).length
    : 0;

  type Row = [string, string | null, ("long" | "mono")?];
  const rows: Row[] = [
    ["ID", `#${piece.id}`, "mono"],
    ["Slug", piece.slug, "mono"],
    ["Content type", formatContentType(piece.content_type)],
    ["Content format", formatContentType(piece.content_format)],
    ["Tone", piece.tone || "—"],
    ["Content origin", piece.content_origin || "—", "mono"],
    [
      "Target word count",
      piece.target_word_count
        ? `${piece.target_word_count.toLocaleString()}${
            wordCount ? ` · current ${wordCount.toLocaleString()}` : ""
          }`
        : null,
    ],
    ["Language", piece.language || "—"],
    ["Publication", piece.publication],
    ["Focus keyword", piece.focus_keyword],
  ];

  return (
    <dl className="meta-list">
        {rows
          .filter(([, v]) => v !== null && v !== undefined && v !== "")
          .map(([k, v, mod]) => (
            <div className={`meta-row ${mod || ""}`} key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
        ))}
    </dl>
  );
}
